import { Fragment, useState, useCallback } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Button,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Breadcrumb from "../../common/breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import CommonLoader from "../../common/commonLoader";
import { useEffect, useRef } from "react";
import { ArrowBack } from "@mui/icons-material";
import {
  getVendorRegistrationById,
  saveOrUpdateVendorRegistration,
  checkVendorRegistrationStatutoryDuplicate,
  vendorRegistrationSendInvite,
  getAllPaymentTerms,
  getAllUnitInspectedBy,
} from "../../api/ap_services";
import {
  CompanyDetails,
} from "./form";
import { useNavigate, useParams } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  stepContent: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1), // Reduced top padding since stepper is sticky
  },
  card: {
    padding: theme.spacing(2),
  },
  stickyStepper: {
    position: "sticky",
    // top: 0,
    top: "5rem",
    zIndex: 5, //1000,
    backgroundColor: "#fff",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
}));



/** Normalize API date to YYYY-MM-DD for date inputs */
const formatDateForInput = (val) => {
  if (val == null || val === "") return "";
  if (typeof val !== "string") return String(val);
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toISOString().slice(0, 10);
  } catch {
    return val;
  }
};

/**
 * Maps API response (getAllVendorEvaluation / getVendorRegistrationById) to form field names
 * so stored data populates the form for editing.
 */
const mapApiResponseToFormValues = (apiObj, defaults) => {
  const raw = Array.isArray(apiObj) ? apiObj[0] : apiObj;
  if (!raw || typeof raw !== "object") return defaults;
  const d = { ...defaults };

  const get = (key) => raw[key];
  const setForm = (formKey, value) => {
    if (value !== undefined && value !== null && value !== "")
      d[formKey] = value;
  };

  // Vendor Registration - Company Details tab
  setForm("companyName", get("companyName"));
  const shareholdingStatusRaw = get("shareholdingStatus");
  if (shareholdingStatusRaw != null && shareholdingStatusRaw !== "") {
    d.shareholdingStatus = Array.isArray(shareholdingStatusRaw)
      ? shareholdingStatusRaw[0] ?? ""
      : String(shareholdingStatusRaw).split(",")[0] ?? String(shareholdingStatusRaw);
  }
  setForm(
    "shareholdingStatusOtherRemarks",
    get("shareholdingStatusOtherRemarks"),
  );
  setForm("natureOfBusiness", get("natureOfBusiness"));
  // Company Address (multiline UI) - default line 1 from legacy officeAddress
  setForm("companyStreetDoorNo", get("companyStreetDoorNo"));
  setForm("companyAddressLine1", get("companyAddressLine1"));
  setForm("companyAddressLine2", get("companyAddressLine2"));
  setForm("companyAddressLine3", get("companyAddressLine3"));
  setForm("companyAddressLine4", get("companyAddressLine4"));
  setForm("companyAddressLine5", get("companyAddressLine5"));
  setForm("companyPostalCode", get("companyPostalCode"));
  setForm("companyCity", get("companyCity"));
  setForm("companyStateRegion", get("companyStateRegion"));
  setForm("companyCountry", get("companyCountry"));

  // Factory Address (multiline UI) - default line 1 from legacy factoryAddress
  setForm("factoryStreetDoorNo", get("factoryStreetDoorNo"));
  setForm("factoryAddressLine1", get("factoryAddress"));
  setForm("factoryAddressLine2", get("factoryAddressLine2"));
  setForm("factoryAddressLine3", get("factoryAddressLine3"));
  setForm("factoryAddressLine4", get("factoryAddressLine4"));
  setForm("factoryAddressLine5", get("factoryAddressLine5"));
  setForm("factoryPostalCode", get("factoryPostalCode"));
  setForm("factoryCity", get("factoryCity"));
  setForm("factoryStateRegion", get("factoryStateRegion"));
  setForm("factoryCountry", get("factoryCountry"));
  setForm("factoryAddressSameAsCompany", get("factoryAddressSameAsCompany"));
  setForm("yearOfEstablishment", get("yearOfEstablishment"));
  setForm("ownerName", get("ownerName"));
  setForm("ownerFathersName", get("ownerFathersName"));
  setForm("ownerContactNo", get("ownerContactNo"));
  setForm("ownerEmail", get("ownerEmail"));
  setForm("sendInvite", get("sendInvite"));
  setForm("partnerName", get("partnerName"));
  setForm("partnerContactNo", get("partnerContactNo"));
  setForm("partnerEmail", get("partnerEmail"));
  setForm("primaryContactName", get("primaryContactName"));
  setForm("primaryContactDesignation", get("primaryContactDesignation"));
  setForm("primaryContactNo", get("primaryContactNo"));
  setForm("primaryContactEmail", get("primaryContactEmail"));
  setForm("secondaryContactName", get("secondaryContactName"));
  setForm("secondaryContactDesignation", get("secondaryContactDesignation"));
  setForm("secondaryContactNo", get("secondaryContactNo"));
  setForm("secondaryContactEmail", get("secondaryContactEmail"));
  setForm("secondaryContactName2", get("secondaryContactName2"));
  setForm("secondaryContactDesignation2", get("secondaryContactDesignation2"));
  setForm("secondaryContactNo2", get("secondaryContactNo2"));
  setForm("secondaryContactEmail2", get("secondaryContactEmail2"));
  setForm("paymentTerms", get("paymentTerms"));
  setForm("unitInspectedBy", get("unitInspectedBy"));
  setForm("vendorEvaluationAttachment", get("vendorEvaluationAttachment"));
  setForm("vendorTypeSubClassification", get("vendorTypeSubClassification"));
  setForm("vendorAgreementStatus", get("vendorAgreementStatus"));




  const vendorTypeIdVal = get("vendorTypeId") ?? get("vendor_type_id");
  const vendorCodeVal = get("vendorTypeCode") ?? get("vendor_code");
  if (vendorTypeIdVal != null && vendorTypeIdVal !== "") {
    setForm("vendorTypeId", vendorTypeIdVal);
  }
  if (vendorCodeVal != null && vendorCodeVal !== "") {
    setForm("vendorTypeCode", vendorCodeVal);
  }
  if (vendorTypeIdVal || vendorCodeVal) {
    setForm("vendorCodeOption", {
      id: vendorTypeIdVal ?? undefined,
      code: vendorCodeVal ?? get("vendorTypeCode"),
      name: get("vendorName") ?? get("vendor_name") ?? "",
    });
  }

  return d;
};

const initialValues = {
  agreeTerms: false,
  overallComments: "",

  vendorTypeId: null,
  vendorTypeCode: "",
  vendorCodeOption: null,
  vendorTypeSubClassification: "",

  // Step 0 - Company Details tab
  companyName: "",
  shareholdingStatus: "",
  shareholdingStatusOtherRemarks: "",
  natureOfBusiness: "",
  // Company Address (multiline)
  companyStreetDoorNo: "",
  companyAddressLine1: "",
  companyAddressLine2: "",
  companyAddressLine3: "",
  companyAddressLine4: "",
  companyAddressLine5: "",
  companyPostalCode: "",
  companyCity: "",
  companyStateRegion: "",
  companyCountry: "INDIA",
  // Factory Address (multiline)
  factoryStreetDoorNo: "",
  factoryAddressLine1: "",
  factoryAddressLine2: "",
  factoryAddressLine3: "",
  factoryAddressLine4: "",
  factoryAddressLine5: "",
  factoryPostalCode: "",
  factoryCity: "",
  factoryStateRegion: "",
  factoryCountry: "INDIA",
  factoryAddressSameAsCompany: false,
  // Legacy single-line fields (kept for backward compatibility / payload)
  officeAddress: "",
  factoryAddress: "",
  yearOfEstablishment: "",
  ownerName: "",
  ownerFathersName: "",
  ownerContactNo: "",
  isOwnerContactNoAlreadyExists: "N",
  ownerEmail: "",
  sendInvite: true,
  partnerName: "",
  partnerContactNo: "",
  partnerEmail: "",
  primaryContactName: "",
  primaryContactDesignation: "",
  primaryContactNo: "",
  primaryContactEmail: "",
  secondaryContactName: "",
  secondaryContactDesignation: "",
  secondaryContactNo: "",
  secondaryContactEmail: "",
  secondaryContactName2: "",
  secondaryContactDesignation2: "",
  secondaryContactNo2: "",
  secondaryContactEmail2: "",
  paymentTerms: "",
  unitInspectedBy: "",
  vendorEvaluationAttachment: null,
  vendorAgreementStatus: "N",
  isEdit: "Y",

};

// Safe transform: avoid val.trim on non-string (prevents "trim is not a function")

const EVALUATION_VENDOR_TYPE_CODES = ["ZJCP", "ZJYF", "ZRTL"];

const requiresVendorEvaluation = (vendorTypeCode) =>
  EVALUATION_VENDOR_TYPE_CODES.includes(String(vendorTypeCode ?? "").trim());

const hasAttachment = (value) => {
  if (value == null || value === "") return false;
  if (value instanceof File) return true;
  if (typeof value === "string" && value.trim() !== "") return true;
  if (typeof value === "object") {
    return Boolean(
      value.name || value.fileName || value.url || value.path || value.size,
    );
  }
  return false;
};

const requiredAttachment = (message) =>
  Yup.mixed()
    .nullable()
    .required(message)
    .test("required-attachment", message, (value) => hasAttachment(value));

const validationSchema = Yup.object({
  // Step 0 - Company Details tab
  companyName: Yup.string().trim().required("Company Name is required"),
  shareholdingStatus: Yup.string()
    .trim()
    .required("Shareholding Status is required"),
  shareholdingStatusOtherRemarks: Yup.string().when("shareholdingStatus", {
    is: (val) => val === "Other",
    then: (schema) =>
      schema
        .trim()
        .required("Please provide description for Other"),
    otherwise: (schema) => schema.nullable(),
  }),

  natureOfBusiness: Yup.string().trim().required("Nature of Business is required"),
  // Company Address validation


  vendorTypeId: Yup.mixed().required("Vendor Group is required"),
  vendorTypeCode: Yup.string().trim().nullable(),
  vendorTypeSubClassification: Yup.string().when("vendorTypeCode", {
    is: (code) => code === "ZJCP" || code === "ZJYF",
    then: (schema) => schema.trim().required("Vendor Classification is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  
  sendInvite: Yup.boolean(),
  ownerName: Yup.string().trim().required("Owner Name is required"),
  ownerFathersName: Yup.string().trim().nullable(),
  ownerContactNo: Yup.string()
    .trim()
    .required("Owner Contact Number is required")
    .matches(/^\d{10}$/, "Owner Contact Number must be exactly 10 digits")
    .test(
      "owner-contact-unique",
      "This owner contact number is already registered for another vendor.",
      function () {
        return this.parent.isOwnerContactNoAlreadyExists !== "Y";
      },
    ),
  isOwnerContactNoAlreadyExists: Yup.string().oneOf(["Y", "N"]).nullable(),
  ownerEmail: Yup.string()
    .trim()
    .email("Invalid email")
    .required("Owner E-mail ID is required"),
  partnerName: Yup.string().trim().nullable(),
  partnerContactNo: Yup.string().trim().nullable().matches(/^\d{10}$/, {
    message: "Owner Contact Number must be exactly 10 digits",
    excludeEmptyString: true,
  }),
  partnerEmail: Yup.string().trim().email("Invalid email").nullable(),
  primaryContactName: Yup.string()
    .trim()
 .required("Primary Contact Name is required"),
    
  primaryContactDesignation: Yup.string()
    .trim()
 .required("Primary Contact Designation is required"),
  primaryContactNo: Yup.string()
    .trim()
    .matches(/^\d{10}$/, "Owner Contact Number must be exactly 10 digits")
   .required("Primary Contact Number is required"),
   
  primaryContactEmail: Yup.string()
    .trim()
    .email("Invalid email")
    .required("Primary Contact E-mail ID is required"),

  secondaryContactName: Yup.string().trim().nullable(),
  secondaryContactDesignation: Yup.string().trim().nullable(),
  secondaryContactNo: Yup.string().trim().nullable().matches(/^\d{10}$/, {
    message: "Owner Contact Number must be exactly 10 digits",
    excludeEmptyString: true,
  }),
  secondaryContactEmail: Yup.string().trim().email("Invalid email").nullable(),
  secondaryContactName2: Yup.string().trim().nullable(),
  secondaryContactDesignation2: Yup.string().trim().nullable(),
  secondaryContactNo2: Yup.string().trim().nullable().matches(/^\d{10}$/, {
    message: "Owner Contact Number must be exactly 10 digits",
    excludeEmptyString: true,
  }),
  secondaryContactEmail2: Yup.string().trim().email("Invalid email").nullable(),
  paymentTerms: Yup.string().trim().required("Payment Terms is required"),
  vendorAgreementStatus: Yup.string().when("vendorTypeCode", {
    is: (code) => code === "ZJCP" || code === "ZJYF",
    then: (schema) => schema.trim().required("Vendor Agreement Status is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  unitInspectedBy: Yup.string().when(["vendorTypeCode", "vendorAgreementStatus"], {
    is: (code, status) => (code === "ZJCP" || code === "ZJYF") && status === "Y",
    then: (schema) => schema.trim().required("Unit Inspected By is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  vendorEvaluationAttachment: Yup.mixed().when(["vendorTypeCode", "vendorAgreementStatus"], {
    is: (code, status) => (code === "ZJCP" || code === "ZJYF") && status === "Y",
    then: () =>
      requiredAttachment("Vendor Evaluation Attachment is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});


const VendorSendInvite = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const { vendorRegistrationId: vendorRegistrationListId } = useParams();
  // const isEdit = Boolean(vendorRegistrationListId);
  const [loading, setLoading] = useState(!!vendorRegistrationListId);
  const stepperRef = useRef(null);
  // Tracks the last successfully-submitted payload, so "Next" can skip API calls
  // when the user didn't change anything in edit mode.
  const lastSavedSignatureRef = useRef(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [inspectedByOptions, setInspectedByOptions] = useState([]);

  // const [vendorRegistrationId, setVendorRegistrationId] = useState(
  //   isEdit ? vendorRegistrationListId : null,
  // );

  const isAdmin = JSON.parse(sessionStorage.getItem("userData"))?.groupId === 1;

  const [ownerContactDuplicateDialog, setOwnerContactDuplicateDialog] =
    useState({
      open: false,
    });
  const openOwnerContactDuplicateDialog = useCallback(() => {
    setOwnerContactDuplicateDialog({ open: true });
  }, []);
  const closeOwnerContactDuplicateDialog = useCallback(() => {
    setOwnerContactDuplicateDialog({ open: false });
  }, []);

  const getFieldGroupIndex = (visibleStep) =>
    isAdmin ? visibleStep : visibleStep === 4 ? 5 : visibleStep;

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    }, 0);
  }, [activeStep]);

  useEffect(() => {
    getAllPaymentTerms(0, 9999)
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setPaymentTermsOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setPaymentTermsOptions([]);
      });

       getAllUnitInspectedBy()
      .then((res) => {
        const list = res?.response?.responseObject ?? [];
        setInspectedByOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setInspectedByOptions([]);
      });
  }, []);

  

  useEffect(() => {
    if (vendorRegistrationListId != null && vendorRegistrationListId !== "") {
      setLoading(true);
      getVendorRegistrationById(vendorRegistrationListId)
        .then((res) => {
          setLoading(false);
          const raw = res?.response?.responseObject ?? res?.response;
          const data = Array.isArray(raw) ? raw[0] : raw;
          const hasValidRecord =
            data &&
            typeof data === "object" &&
            (data.vendorRegistrationId != null || data.id != null);
          if (hasValidRecord) {
            setSelectedForEdit(data);
            const id = data.vendorRegistrationId ?? data.id;
            // if (id != null) setVendorRegistrationId(id);
            const savedStep =
              data.currentStep ?? data.activeStep ?? data.current_step ?? 0;
            const stepNum = Math.min(
              Math.max(0, Number(savedStep)),
       
            );
            if (!Number.isNaN(stepNum)) {
              setActiveStep(stepNum);
              setMaxStepReached(stepNum);
            }
          } else {
            setSelectedForEdit(null);
          }
        })
        .catch(() => {
          setLoading(false);
          setSelectedForEdit(null);
        });
    } else {
      setSelectedForEdit(null);
    }
  }, [vendorRegistrationListId]);

  const stepFieldGroups = [
    // Step 0 - Company Details tab
    [
      "companyName",
      "shareholdingStatus",
      "shareholdingStatusOtherRemarks",
      "natureOfBusiness",
      "companyStreetDoorNo",
      "companyAddressLine1",
      "companyAddressLine2",
      "companyAddressLine3",
      "companyAddressLine4",
      "companyAddressLine5",
      "companyPostalCode",
      "companyCity",
      "companyStateRegion",
      "companyCountry",
      "officeAddress",
      "vendorTypeId",
      "vendorTypeCode",
      "vendorTypeSubClassification",
      "yearOfEstablishment",
      "sendInvite",
      "ownerName",
      "ownerFathersName",
      "ownerContactNo",
      "ownerEmail",
      "partnerName",
      "partnerContactNo",
      "partnerEmail",
      "primaryContactName",
      "primaryContactDesignation",
      "primaryContactNo",
      "primaryContactEmail",
      "secondaryContactName",
      "secondaryContactDesignation",
      "secondaryContactNo",
      "secondaryContactEmail",
      "secondaryContactName2",
      "secondaryContactDesignation2",
      "secondaryContactNo2",
      "secondaryContactEmail2",
      "unitInspectedBy",
      "vendorAgreementStatus",
      "vendorEvaluationAttachment",
      "isEdit"
    ]

  ];



  const handleSubmitApi = async (values, resetForm) => {
    setLoading(true);

    // Log all form data when Submit is clicked (check if all data is saved)
    console.log("All form values:", values);
    // Summary: which fields have values (useful for file/image fields)
    const filledKeys = Object.keys(values).filter(
      (k) => values[k] !== null && values[k] !== undefined && values[k] !== "",
    );
    console.log("Fields with values (" + filledKeys.length + "):", filledKeys);
    // Image/file fields summary (File objects don't stringify well)

    try {
      const payload = { ...values };

      // if (vendorRegistrationId) {
      //   payload.vendorRegistrationId = vendorRegistrationId;
      // }

     

      console.log("Final payload:", payload);

      const res = await saveOrUpdateVendorRegistration(payload);

      console.log("API Response:", res);

      toast.success("Vendor registration saved successfully!");

      const isVendorUser =
        JSON.parse(sessionStorage.getItem("userData")).groupId === 0;
      const isAdminUser =
        JSON.parse(sessionStorage.getItem("userData")).groupId === 1;
      const hasVendorCode =
        isVendorUser &&
        JSON.parse(sessionStorage.getItem("userData"))?.hasVendorCode === "Y";
      if (isVendorUser && !hasVendorCode) {
        setActiveStep(0);
        navigate(
          `${process.env.PUBLIC_URL}/master/vendorRegistrationForm/${JSON.parse(sessionStorage.getItem("userData")).userId}`,
        );
      } else {
        if (isAdminUser) {
          resetForm();
          // redirect to list page
          navigate(`${process.env.PUBLIC_URL}/master/vendorRegistrationList`);
        } else {
          navigate(`${process.env.PUBLIC_URL}/dashboard`);
        }
      }
    } catch (error) {
      toast.error("Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // const noDataFound =
  //   isEdit && !loading && selectedForEdit === null && vendorRegistrationListId;


  return (
    <Fragment>
      {loading && <CommonLoader />}
      <Breadcrumb
        title="Vendor Send Invite Form"
        parent={
          //onCancel ? (
          isAdmin && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                //variant="outlined"
                color={"secondary"}
                variant="contained"
                type="button"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                size="small"
              >
                Back to List
              </Button>
            </div>
          )
          // ) : null
        }
      />

      <Container fluid={true}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.root}>
              <Formik
                key={selectedForEdit?.vendorRegistrationId ?? "new"}
                initialValues={
                  selectedForEdit
                    ? mapApiResponseToFormValues(selectedForEdit, initialValues)
                    : initialValues
                }
                validationSchema={validationSchema}
                enableReinitialize
                validateOnChange={true}
                validateOnBlur={true}
                onSubmit={(values, { resetForm }) =>
                  handleSubmitApi(values, resetForm)
                }
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  setFieldValue,
                  validateForm,
                  setTouched,
                  resetForm,
                  handleBlur,
                  setFieldError,
                }) => {
                  const handleOwnerContactBlur = (e) => {
                    handleBlur(e);
                    const raw = values.ownerContactNo;
                    if (raw == null || String(raw).trim() === "") {
                      setFieldValue("isOwnerContactNoAlreadyExists", "N");
                      return;
                    }
                    const trimmed = String(raw).trim();
                    if (!/^\d{10}$/.test(trimmed)) {
                      return;
                    }
                    const originalTrimmed =
                      selectedForEdit?.ownerContactNo != null
                        ? String(selectedForEdit.ownerContactNo).trim()
                        : "";
                    if (
                      vendorRegistrationListId != null &&
                      vendorRegistrationListId !== "" &&
                      originalTrimmed !== "" &&
                      trimmed === originalTrimmed
                    ) {
                      return;
                    }
                    checkVendorRegistrationStatutoryDuplicate(
                      "OWNER_CONTACT",
                      trimmed,
                      vendorRegistrationListId,
                    )
                      .then((res) => {
                        if (res.exists) {
                          setFieldValue("isOwnerContactNoAlreadyExists", "Y");
                          setFieldError(
                            "ownerContactNo",
                            "This owner contact number is already registered for another vendor.",
                          );
                          openOwnerContactDuplicateDialog();
                        } else {
                          setFieldValue("isOwnerContactNoAlreadyExists", "N");
                        }
                      })
                      .catch(() =>
                        setFieldValue("isOwnerContactNoAlreadyExists", "N"),
                      );
                  };

                  const handleSendInvite = async () => {
                    // Validate only the invite-related fields
                    const inviteFields = [
                      "companyName",
                      "shareholdingStatus",
                      "shareholdingStatusOtherRemarks",
                      "vendorTypeId",
                      "vendorTypeCode",
                      "vendorTypeSubClassification",
                      "natureOfBusiness",
                      "ownerName",
                      "ownerContactNo",
                      "ownerEmail",
                      "paymentTerms",
                      "unitInspectedBy",
                      "vendorAgreementStatus",
                      "vendorEvaluationAttachment",
                    ];
                    const inviteSchema = validationSchema.pick(inviteFields);

                    let inviteErrors = {};
                    try {
                      await inviteSchema.validate(values, {
                        abortEarly: false,
                      });
                    } catch (err) {
                      if (err.inner) {
                        err.inner.forEach((e) => {
                          if (e.path) inviteErrors[e.path] = e.message;
                        });
                      }
                    }

                    if (values.isOwnerContactNoAlreadyExists === "Y") {
                      toast.error(
                        "This owner contact number is already registered for another vendor.",
                      );
                      setTouched({ ...touched, ownerContactNo: true });
                      return;
                    }

                    const errorKeys = Object.keys(inviteErrors);
                    if (errorKeys.length > 0) {
                      const newTouched = { ...touched };
                      errorKeys.forEach((key) => {
                        newTouched[key] = true;
                      });
                      setTouched(newTouched);
                      toast.error(
                        "Please fill all mandatory invite fields before sending invite.",
                      );
                      return;
                    }

                    try {
                      setLoading(true);
                      const payload = {
                        companyName: values.companyName,
                        shareholdingStatus: values.shareholdingStatus,
                        shareholdingStatusOtherRemarks:
                          values.shareholdingStatusOtherRemarks,
                        vendorTypeId: values.vendorTypeId,
                        vendorTypeCode: values.vendorTypeCode,
                        vendorTypeSubClassification: values.vendorTypeSubClassification,
                        natureOfBusiness: values.natureOfBusiness,
                        ownerName: values.ownerName,
                        ownerFathersName: values.ownerFathersName,
                        ownerContactNo: values.ownerContactNo,
                        ownerEmail: values.ownerEmail,
                        partnerName: values.partnerName,
                        partnerContactNo: values.partnerContactNo,
                        partnerEmail: values.partnerEmail,
                        primaryContactName: values.primaryContactName,
                        primaryContactDesignation: values.primaryContactDesignation,
                        primaryContactNo: values.primaryContactNo,
                        primaryContactEmail: values.primaryContactEmail,
                        secondaryContactName: values.secondaryContactName,
                        secondaryContactDesignation:
                          values.secondaryContactDesignation,
                        secondaryContactNo: values.secondaryContactNo,
                        secondaryContactEmail: values.secondaryContactEmail,
                        secondaryContactName2: values.secondaryContactName2,
                        secondaryContactDesignation2:
                          values.secondaryContactDesignation2,
                        secondaryContactNo2: values.secondaryContactNo2,
                        secondaryContactEmail2: values.secondaryContactEmail2,
                        paymentTerms: values.paymentTerms,
                        sendInvite: values.sendInvite,
                        unitInspectedBy: values.unitInspectedBy,
                        vendorAgreementStatus: values.vendorAgreementStatus,
                        vendorEvaluationAttachment:
                          values.vendorEvaluationAttachment instanceof File
                            ? values.vendorEvaluationAttachment.name
                            : values.vendorEvaluationAttachment || "",
                          isEdit: values.isEdit

                      };
                      await vendorRegistrationSendInvite(payload);
                      toast.success("Invite sent successfully.");

                      resetForm();

                      setTimeout(() => {
                        navigate(
                          `${process.env.PUBLIC_URL}/master/vendorRegistrationList`,
                        );
                      }, 1200);
                    } catch (err) {
                      toast.error(
                        err?.response?.data?.message ||
                          "Failed to send invite. Please try again.",
                      );
                    } finally {
                      setLoading(false);
                    }
                  };

                  return (
                    <Form>
                      <div className={classes.stepContent}>
                
                      <CompanyDetails
                            formData={values}
                            onFieldChange={handleChange}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            onOwnerContactBlur={handleOwnerContactBlur}
                            paymentTermsOptions={paymentTermsOptions}
                            inspectedByOptions={inspectedByOptions}
                            groupId={{
                              isAdmin,
                            }}
                     
                          />
 
 
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSendInvite}
                          type="button"
                          disabled={loading}
                          sx={{ mr: 0, mt: 2, }}
                        >
                          Send Invite
                        </Button>
                                    
                                 
                         
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </CardContent>
        </Card>
      </Container>
      <ToastContainer autoClose={2000} />
      <Dialog
        open={ownerContactDuplicateDialog.open}
        onClose={closeOwnerContactDuplicateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Owner Contact Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This owner contact number is already registered for another vendor.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Please enter a different owner contact number before sending the
            invite.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOwnerContactDuplicateDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default VendorSendInvite;
